import { AfterViewInit, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ValidationHandlerService, TranslatedToastrService, EditComponent, BaseComponent, LanguageService, DataTableRequest, OneColumnSearchData, ImgWithSrcComponentFixWidth75px } from 'lib/Vestra.shared';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AuthenticationService, VestraService } from 'src/app/services/api-services';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TranslateService } from '@ngx-translate/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { ADTSettings } from 'angular-datatables/src/models/settings';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
    selector: 'app-coupon-list',
    templateUrl: './coupon-list.component.html',
    styleUrls: ['./coupon-list.component.scss'],
    standalone: false,
})
export class CouponListComponent extends BaseComponent implements OnInit, AfterViewInit {
    @ViewChild('actionsRef') actionsRef: TemplateRef<EditComponent>;
    @ViewChild('imgWidth75pxRef_couponImage') imgWidth75pxRef_couponImage: TemplateRef<ImgWithSrcComponentFixWidth75px>;
    @ViewChild('imgWidth75pxRef_couponGif') imgWidth75pxRef_couponGif: TemplateRef<ImgWithSrcComponentFixWidth75px>;


    private partnerId: number;
    public partnerName: string;

    constructor(
        protected override validationHandlerService: ValidationHandlerService,
        private translatedToastrService: TranslatedToastrService,
        private translateService: TranslateService,
        protected override modalService: BsModalService,
        private vestraService: VestraService,
        private authenticationService: AuthenticationService,
        protected override spinnerService: NgxSpinnerService,
        protected override languageService: LanguageService,
        private router: Router,
        private route: ActivatedRoute
    ) {
        super(spinnerService, modalService, validationHandlerService, translateService, languageService);
    }

    public ngOnInit(): void {
        this.route.params.subscribe(params => {
            this.partnerId = parseInt(params['id']);
            this.handleSpinner(
                firstValueFrom(this.vestraService.getPartnerExistById(params['id']))
                    .then(res => {
                        if (res.isSuccess == false) {
                            this.router.navigate([`/administration/home`]);
                        }

                        this.partnerName = res.result;
                        return Promise.resolve(res);
                    })
                    .catch((err: HttpErrorResponse) => {
                        this.validationHandlerService.handleServerError(err);
                    })
            );
        });

        this.dataTableOnInit();
    }

    public ngAfterViewInit() {
        this.dataTableAfterViewInit();
    }

    public ngOnDestroy() {
        this.dataTableOnDestroy();
    }

    public override getDtOptions() {
        this.languageUrl = this.languageService.getTranslationUrl();
        const self = this;
        const res: ADTSettings = {
            serverSide: true,
            processing: false,
            pageLength: 20,
            lengthChange: false,
            responsive: {
                details: {
                    type: 'column',
                    target: -1,
                },
            },
            searching: false,
            destroy: true,
            language: {
                url: this.languageUrl,
            },
            ajax: (dataTablesParams: any, callback: any) => {
                const searchData: OneColumnSearchData = {
                    propertyValue: this.searchValue,
                };

                for (let order of dataTablesParams.order) {
                    order.name = dataTablesParams.columns[order.column]?.data;
                }

                const model: DataTableRequest = {
                    draw: dataTablesParams.draw,
                    start: dataTablesParams.start,
                    length: dataTablesParams.length,
                    order: dataTablesParams.order,
                    search: searchData,
                    columns: this.transformToColumnData(this.dtOptions.columns),
                };

                this.datatableRequest = model;

                if (!this.partnerId) {
                    return;
                }

                this.handleSpinner(firstValueFrom(this.vestraService.getCouponListDataSourceByPartner(this.partnerId, model)))
                    .then(res => {
                        callback(this.getTableData(res));
                        return Promise.resolve(res);
                    })
                    .catch((err: HttpErrorResponse) => {
                        this.validationHandlerService.handleServerError(err);
                        callback(this.getEmptyTableData());
                    });
            },
            columns: [
                {
                    data: 'id',
                    title: 'common.id',
                    searchable: false,
                    orderable: true,
                },
                {
                    data: 'redeemType',
                    title: 'coupon.redeem_type',
                    searchable: false,
                    orderable: true,
                    render: function (data, type, row, meta) {
                        let translateString = "coupon_redeem_type.not_set";
                        if(data)
                        {
                            if(data == 0)
                            {
                                translateString =  "coupon_redeem_type.not_set";
                            }
                            else if(data == 20)
                            {
                                translateString =  "coupon_redeem_type.online";
                            }
                            else if(data == 40)
                            {
                                translateString =  "coupon_redeem_type.qr_code";
                            }
                            else if(data == 60)
                            {
                                translateString =  "coupon_redeem_type.image_or_gif";
                            }
                        }
                        return self.translateService.instant(translateString)
                    },
                },
                {
                    data: 'redirectUrl',
                    title: 'coupon.redirect_url',
                    searchable: false,
                    orderable: true,
                    render: function (data, type, row, meta) {
                        if(data)
                        {
                            return self.translateService.instant("common.yes")
                        }
                        return self.translateService.instant("common.no")
                    },
                },
                {
                    data: 'couponCode',
                    title: 'coupon.code',
                    searchable: false,
                    orderable: true,
                },
                {
                    data: 'description',
                    title: 'coupon.description',
                    searchable: false,
                    orderable: true,
                },
                {
                    data: 'price',
                    title: 'coupon.price',
                    searchable: false,
                    orderable: true,
                },
                {
                    data: 'discountPrice',
                    title: 'coupon.discount_price',
                    searchable: false,
                    orderable: true,
                    render: function (data, type, row, meta) {
                        if (data === null) {
                            return '';
                        }
                        return data;
                    },
                },
                {
                    data: 'discountPercentage',
                    title: 'coupon.discount_percentage',
                    searchable: false,
                    orderable: true,
                    render: function (data, type, row, meta) {
                        if (data === null) {
                            return '';
                        }
                        return data + '%';
                    },
                },
                {
                    data: 'expiryDate',
                    title: 'coupon.expired_date',
                    searchable: false,
                    orderable: true,
                    render: (data: string) => {
                        if (!data) return '';
                        const date = new Date(data);
                        return date.toLocaleDateString();
                    },
                },
                {
                    data: 'couponImageUrl',
                    title: 'coupon.image',
                    responsivePriority: 1,
                    searchable: false,
                    orderable: false,
                    ngTemplateRef: {
                        ref: this.imgWidth75pxRef_couponImage,
                    },
                },
                {
                    data: 'couponGifUrl',
                    title: 'coupon.gif',
                    responsivePriority: 1,
                    searchable: false,
                    orderable: false,
                    ngTemplateRef: {
                        ref: this.imgWidth75pxRef_couponGif,
                    },
                },
                {
                    data: 'count',
                    title: 'coupon.count',
                    searchable: false,
                    orderable: true,
                },
                {
                    data: 'isActive',
                    title: 'common.status',
                    responsivePriority: 2,
                    searchable: false,
                    orderable: true,
                    render: function (data, type, row, meta) {
                        return data ? '<img class="action-icon" src="../../../../assets/icon/check-mark.png"/>' : '<img class="action-icon" src="../../../../assets/icon/cross.png"/>';
                    },
                },
                {
                    data: 'isTopWeekly',
                    title: 'coupon.is_top_weekly',
                    responsivePriority: 2,
                    searchable: false,
                    orderable: true,
                    render: function (data, type, row, meta) {
                        return data ? '<img class="action-icon" src="../../../../assets/icon/check-mark.png"/>' : '<img class="action-icon" src="../../../../assets/icon/cross.png"/>';
                    },
                },
                {
                    data: 'isCarousel',
                    title: 'coupon.is_carousel',
                    responsivePriority: 2,
                    searchable: false,
                    orderable: true,
                    render: function (data, type, row, meta) {
                        return data ? '<img class="action-icon" src="../../../../assets/icon/check-mark.png"/>' : '<img class="action-icon" src="../../../../assets/icon/cross.png"/>';
                    },
                },
                {
                    title: 'common.actions',
                    data: null,
                    searchable: false,
                    orderable: false,
                    defaultContent: '',
                    ngTemplateRef: {
                        ref: this.actionsRef,
                        context: {
                            captureEvents: this.onEditItem.bind(this),
                        },
                    },
                    responsivePriority: 1,
                },
                {
                    data: '',
                    orderable: false,
                    responsivePriority: 1,
                    render: function (data) {
                        return '';
                    },
                },
            ],
            columnDefs: [
                {
                    targets: '_all',
                    visible: true,
                },
                {
                    className: 'details-icon dtr-control',
                    orderable: false,
                    target: -1,
                },
            ],
        };

        return res;
    }

    public onSearch(event: string): void {
        this.searchValue = event;
        this.refreshDataTable();
    }

    public onAddItem() {
        this.navigateToCouponEditPage(0);
    }

    public onEditItem(event: any) {
        this.navigateToCouponEditPage(event.data.id);
    }

    public navigateToCouponEditPage(id: number | null) {
        this.router.navigate(['/administration/coupon-edit', this.partnerId, id]);
    }
}
